import React from 'react';
import { Layers, History, ScrollText, Eye, Save, X, Send, Cpu } from 'lucide-react';
import { primaryButton, secondaryButton } from '../ui';
import { TourButton } from '../tour/TourButton';
import { FORM_TAG_LABELS, FormTag } from '../types';

type EditorHeaderProps = {
  editing: boolean;
  title?: string;
  sensorType?: string;
  category?: string;
  tags: FormTag[];
  /** When set (existing forms), the calibration type badges become toggles; saved with "Save draft". */
  onTagsChange?: (tags: FormTag[]) => void;
  onCancel: () => void;
  onVersions: () => void;
  onAudit: () => void;
  onPreview: () => void;
  onSave: () => void;
  onPublish: () => void;
};

const ALL_TAGS: FormTag[] = ['single_calibration', 'bulk_calibration'];

export default function EditorHeader({ editing, title, sensorType, category, tags, onTagsChange, onCancel, onVersions, onAudit, onPreview, onSave, onPublish }: EditorHeaderProps) {
  return (
      <header data-tour="editor-header" className="flex shrink-0 items-center justify-between gap-4 border-b border-slate-200 bg-white px-4 py-3 md:px-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary-600">
            <Layers className="h-3.5 w-3.5" /> Form builder
          </div>
          <div className="mt-0.5 flex items-center gap-2">
            <h1 className="truncate text-base font-semibold text-slate-900">
              {title || (editing ? 'Edit form' : 'Create form')}
            </h1>
            {sensorType && (
              <span title="Sensor type — set at creation" className="inline-flex shrink-0 items-center gap-1 rounded-md bg-primary-50 px-1.5 py-0.5 text-[10px] font-semibold text-primary-700">
                <Cpu className="h-3 w-3" /> {sensorType}
              </span>
            )}
            {category && (
              <span title="Category — set at creation" className="inline-flex shrink-0 items-center rounded-md bg-primary-50 px-1.5 py-0.5 text-[10px] font-semibold text-primary-700">
                {category}
              </span>
            )}
            {onTagsChange
              ? ALL_TAGS.map((tag) => {
                  const on = tags.includes(tag);
                  return (
                    <button key={tag} type="button" aria-pressed={on} title={on ? 'Click to remove (save the draft to apply)' : 'Click to add (save the draft to apply)'}
                      onClick={() => { const next = on ? tags.filter((t) => t !== tag) : [...tags, tag]; if (next.length) onTagsChange(next); }}
                      className={`shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-medium transition ${on ? 'bg-slate-900 text-white' : 'border border-slate-200 bg-white text-slate-500 hover:bg-slate-50'}`}>
                      {FORM_TAG_LABELS[tag] || tag}
                    </button>
                  );
                })
              : tags.map((tag) => (
                  <span key={tag} title="Calibration type — set at creation" className="shrink-0 rounded-md bg-slate-900 px-1.5 py-0.5 text-[10px] font-medium text-white">
                    {FORM_TAG_LABELS[tag] || tag}
                  </span>
                ))}
            {editing && (
              <span className="rounded-md bg-amber-50 px-1.5 py-0.5 font-mono text-[9px] font-medium text-amber-700">
                draft
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            className={secondaryButton}
            onClick={onCancel}
          >
            <X className="h-3.5 w-3.5" /> Cancel
          </button>
          {editing && (
            <button className={secondaryButton} onClick={onVersions}>
              <History className="h-3.5 w-3.5" /> Versions
            </button>
          )}
          {editing && (
            <button className={secondaryButton} onClick={onAudit}>
              <ScrollText className="h-3.5 w-3.5" /> Audit logs
            </button>
          )}
          <TourButton id="editor" />
          <button data-tour="editor-preview" className={secondaryButton} onClick={onPreview}>
            <Eye className="h-3.5 w-3.5" /> Preview
          </button>
          <button data-tour="editor-save" className={primaryButton} onClick={onSave}>
            <Save className="h-3.5 w-3.5" /> Save draft
          </button>
          {editing && (
            <button
              data-tour="editor-publish"
              className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-slate-900 px-3.5 text-[12px] font-semibold text-white shadow-sm transition hover:bg-slate-800"
              onClick={onPublish}
            >
              <Send className="h-3.5 w-3.5" /> Publish
            </button>
          )}
        </div>
      </header>
  );
}
